import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import EditableText from '@/components/EditableText';
import { Target, Eye, Users, MapPin, Heart, Handshake } from 'lucide-react';

const SECTIONS = [
  { id: 'presentation', label: 'Présentation', icon: Heart },
  { id: 'mission', label: 'Mission & Vision', icon: Target },
  { id: 'equipe', label: 'Équipe', icon: Users },
  { id: 'zones', label: "Zones d'intervention", icon: MapPin },
  { id: 'partenaires', label: 'Partenaires', icon: Handshake },
];

export default function AboutPage() {
  const location = useLocation();

  // Scroll to anchor section when hash changes — even when already on the page.
  // Without this, clicking a sub-menu link with the same pathname does nothing.
  useEffect(() => {
    const hash = location.hash?.replace('#', '');
    if (!hash) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    // Use rAF to ensure the target is mounted before scrolling
    requestAnimationFrame(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [location.pathname, location.hash, location.key]);
  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <section className="section-padding bg-[#F9F8F6]" data-testid="about-page">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#2E332D] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              À propos de CRESA ASBL
            </h1>
            <p className="text-base text-[#5C665A] leading-relaxed">
              <EditableText contentKey="about.intro">
                Carrefour de Réflexion Santé — une organisation de droit congolais
                œuvrant pour le développement intégral auto-géré en RD Congo.
              </EditableText>
            </p>
          </div>

          {/* Quick anchors */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const isActive = location.hash === `#${s.id}`;
              return (
                <Link key={s.id} to={`/a-propos#${s.id}`}
                  data-testid={`about-anchor-${s.id}`}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border-2 transition-all ${
                    isActive
                      ? 'bg-[#4A6B48] border-[#4A6B48] text-white'
                      : 'border-[#E5E3DB] text-[#4A6B48] hover:border-[#4A6B48] hover:bg-[#4A6B48]/5'
                  }`}>
                  <Icon size={14} />
                  {s.label}
                </Link>
              );
            })}
          </div>

          {/* Présentation */}
          <article id="presentation" className="card mb-8 scroll-mt-24" data-testid="about-presentation">
            <div className="flex items-center gap-3 mb-4">
              <Heart className="text-[#D87D4A]" size={28} />
              <h2 className="text-3xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Présentation
              </h2>
            </div>
            <p className="text-[#5C665A] leading-relaxed mb-3">
              Le <strong>Carrefour de Réflexion Santé</strong> (CRESA asbl), aussi appelé
              <em> « Baraza la Afia »</em> en swahili, est une organisation sans but lucratif
              de droit congolais créée en 2010.
            </p>
            <p className="text-[#5C665A] leading-relaxed mb-3">
              Née autour de grands questionnements sur les problématiques de développement
              durable et d&apos;accès aux soins de santé pour tous, CRESA s&apos;est imposée comme un
              acteur de référence pour la promotion des Îlots de Développement Intégral
              Auto-Gérés en RD Congo.
            </p>
            <p className="text-[#5C665A] leading-relaxed italic border-l-4 border-[#D87D4A] pl-4 mt-4">
              « Réflexion sans actions est du verbiage et actions sans réflexion est une agitation. »
              <span className="block text-xs not-italic mt-1 text-[#4A6B48] font-bold">— Devise CRESA ASBL</span>
            </p>
          </article>

          {/* Mission & Vision */}
          <article id="mission" className="card mb-8 scroll-mt-24" data-testid="about-mission">
            <div className="flex items-center gap-3 mb-4">
              <Target className="text-[#4A6B48]" size={28} />
              <h2 className="text-3xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Mission & Vision
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-bold text-[#4A6B48] mb-2 inline-flex items-center gap-2">
                  <Target size={16} /> Mission
                </h3>
                <p className="text-[#5C665A] leading-relaxed text-sm">
                  <EditableText contentKey="about.mission.text">
                    CRESA est une organisation de haute réflexion, d&apos;accompagnement et de
                    capacitation technique pour les organisations locales et nationales, en
                    vue de mobilisations de masse autour des idées-forces de changement et
                    de transformation sociale.
                  </EditableText>
                </p>
              </div>
              <div>
                <h3 className="font-bold text-[#4A6B48] mb-2 inline-flex items-center gap-2">
                  <Eye size={16} /> Vision
                </h3>
                <p className="text-[#5C665A] leading-relaxed text-sm">
                  <EditableText contentKey="about.vision.text">
                    Faire émerger un nouveau paradigme de développement en RDC fondé sur les
                    Îlots de Développement Intégral auto-gérés, dans lesquels les communautés
                    prennent en main leur propre avenir économique, sanitaire et écologique.
                  </EditableText>
                </p>
              </div>
            </div>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
              {[
                'Production agricole & élevage',
                'Sensibilisation FPT',
                'Réseautage',
                'Évaluation participative',
              ].map((axis, i) => (
                <div key={axis} className="bg-[#4A6B48]/5 border-l-4 border-[#4A6B48] p-3 rounded-r">
                  <span className="text-xs font-bold text-[#4A6B48]">AXE {i + 1}</span>
                  <p className="text-[#2E332D] font-semibold mt-1">{axis}</p>
                </div>
              ))}
            </div>
          </article>

          {/* Équipe */}
          <article id="equipe" className="card mb-8 scroll-mt-24" data-testid="about-team">
            <div className="flex items-center gap-3 mb-4">
              <Users className="text-[#4A6B48]" size={28} />
              <h2 className="text-3xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Équipe
              </h2>
            </div>
            <p className="text-[#5C665A] leading-relaxed">
              L&apos;équipe CRESA est composée de membres permanents, de bénévoles, de chercheurs
              et de partenaires locaux mobilisés autour des îlots de développement intégral.
              Notre équipe terrain pilote actuellement le premier spécimen d&apos;îlot dans le
              Sud-Kivu (Territoire de Kabare, Groupement de Bushumba) et prépare l&apos;extension
              aux provinces du Haut-Katanga, de Kinshasa et du Kongo Central.
            </p>
            <p className="text-[#5C665A] leading-relaxed mt-3 italic">
              Pour rencontrer les membres et coordinateurs de l&apos;équipe, contactez-nous via
              la <a href="/contact" className="text-[#4A6B48] font-bold hover:underline">page Contact</a>.
            </p>
          </article>

          {/* Zones d'intervention */}
          <article id="zones" className="card scroll-mt-24" data-testid="about-zones">
            <div className="flex items-center gap-3 mb-4">
              <MapPin className="text-[#D87D4A]" size={28} />
              <h2 className="text-3xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Zones d&apos;intervention
              </h2>
            </div>
            <p className="text-[#5C665A] leading-relaxed mb-4">
              CRESA dispose actuellement de <strong>4 provinces pilotes</strong> où elle déploie
              progressivement son modèle d&apos;îlot de développement intégral :
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              {[
                { name: 'Sud-Kivu', status: 'Îlot pilote actif (Bushumba)', color: '#4A6B48' },
                { name: 'Haut-Katanga', status: 'Base FPT établie', color: '#D87D4A' },
                { name: 'Kinshasa', status: 'Base FPT établie', color: '#D87D4A' },
                { name: 'Kongo Central', status: 'Base FPT établie', color: '#D87D4A' },
              ].map((zone) => (
                <div key={zone.name} className="border-2 border-[#E5E3DB] rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: zone.color }}></span>
                    <h3 className="font-bold text-[#2E332D]">{zone.name}</h3>
                  </div>
                  <p className="text-xs text-[#5C665A]">{zone.status}</p>
                </div>
              ))}
            </div>
            <a href="/carte" className="inline-flex items-center gap-2 text-[#4A6B48] font-bold hover:underline" data-testid="about-go-to-map">
              <MapPin size={16} /> Voir la carte interactive
            </a>
          </article>

          {/* Partenaires */}
          <article id="partenaires" className="card scroll-mt-24" data-testid="about-partners">
            <div className="flex items-center gap-3 mb-4">
              <Handshake className="text-[#4A6B48]" size={28} />
              <h2 className="text-3xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Partenaires locaux, nationaux et internationaux
              </h2>
            </div>
            <p className="text-[#5C665A] leading-relaxed mb-6">
              <EditableText contentKey="about.partners.intro">
                CRESA s&apos;appuie sur un réseau de partenaires de terrain et d&apos;appuis
                techniques et financiers pour propulser son modèle d&apos;îlots de développement
                intégral auto-gérés.
              </EditableText>
            </p>

            <h3 className="text-lg font-bold text-[#4A6B48] mb-3">
              Partenaires de terrain (actions co-réalisées)
            </h3>
            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              {[
                { name: 'Communautés et structures locales', desc: 'Interagissant avec CRESA dans les provinces pilotes sur les 4 axes thématiques' },
                { name: 'Institut de Développement Rural (ISDR/Bukavu)', desc: 'Institution académique partenaire' },
                { name: 'Université de la Nouvelle Pâques (UNP)', desc: 'Institution académique partenaire' },
                { name: 'Mutuelles de Santé Communautaire (REMUSACO)', desc: 'Réseau de mutuelles de santé communautaire' },
                { name: 'CGAT / Kongo Central', desc: "Cellule de Gestion et d'Appui Technique aux Mutuelles de Santé" },
                { name: 'Autorités administratives et coutumières locales', desc: 'Partenaires institutionnels de terrain' },
                { name: 'Associations & composantes plurielles de la société civile', desc: 'Acteurs partenaires sur le terrain' },
              ].map((p) => (
                <div key={p.name} className="border-2 border-[#E5E3DB] rounded-lg p-3 hover:border-[#4A6B48] transition-colors">
                  <h4 className="font-bold text-[#2E332D] text-sm">{p.name}</h4>
                  <p className="text-xs text-[#5C665A] mt-1">{p.desc}</p>
                </div>
              ))}
            </div>

            <h3 className="text-lg font-bold text-[#4A6B48] mb-3">
              Partenaires techniques et financiers
            </h3>
            <div className="grid sm:grid-cols-3 gap-3">
              {[
                { name: 'Solidarité Mondiale (WSM)', desc: 'Partenaire financier et technique' },
                { name: 'Wallonie Bruxelles Internationale (WBI)', desc: 'Partenaire financier' },
                { name: 'Solidarité Santé Sud (SSS)', desc: 'Partenaire financier et technique' },
              ].map((p) => (
                <div key={p.name} className="bg-[#4A6B48]/5 border-l-4 border-[#4A6B48] p-3 rounded-r">
                  <h4 className="font-bold text-[#2E332D] text-sm">{p.name}</h4>
                  <p className="text-xs text-[#5C665A] mt-1">{p.desc}</p>
                </div>
              ))}
            </div>
          </article>
        </div>
      </section>
      <Footer />
    </div>
  );
}
